/**
 * A KULCSBEVITEL HÁROM KÖZÖS DARABJA — hibaágak, cégkereső, négy mező.
 *
 * ⚠️ KÉT FELÜLET RAKJA ÖSSZE ŐKET (#25/4): a Beállítások lap NAV-szelete
 * (`NavSettings` — ez a fő út) és a paletta ablaka (`NavCredentialsDialog`
 * — gyorsítás). A DARABOK UGYANAZOK, csak a keret más: a lapon két hasáb,
 * az ablakban egy oszlop.
 *
 * Amiért külön fájl: a négy mező szabályai (`autoComplete="off"`, a
 * visszaolvashatatlanság kimondása, a felülírás figyelmeztetése) TITKOT
 * védenek. Két példányban élve az egyik előbb-utóbb lemarad — és a
 * lemaradás néma.
 */

import { useTranslation } from 'react-i18next';
import { PasswordInput } from '@/components/common';
import type { NavCredentialEditor } from './useNavCredentialEditor';
import type { NavCredentialStatus } from './useNavCredentials';

/**
 * A MEGÁLLÍTÓ ÉS A KÍSÉRŐ HIBAÁGAK.
 *
 * Nem szűr aszerint, melyik ág állítja meg a munkát: a `listFailed` és a
 * `forbidden` után nincs mit kiválasztani, a `failed`/`invalid` után van —
 * a hívó dönti el, mit rajzol melléjük.
 */
export function NavCredentialNotices({
  status,
  detail,
}: Pick<NavCredentialEditor, 'status' | 'detail'>) {
  const { t } = useTranslation();

  return (
    <>
      {status === 'listFailed' ? <p className="error">{t('navCredentials.listFailed')}</p> : null}
      {status === 'forbidden' ? <p className="error">{t('navCredentials.forbidden')}</p> : null}
      {status === 'keyRingMissing' ? (
        <p className="error">{t('navCredentials.keyRingMissing')}</p>
      ) : null}
      {status === 'failed' ? <p className="error">{t('navCredentials.failed')}</p> : null}
      {status === 'invalid' ? (
        <p className="error">{t('navCredentials.invalid', { detail })}</p>
      ) : null}
    </>
  );
}

interface NavCompanyPickerProps {
  editor: NavCredentialEditor;
  /** Egyedi előtag az `id`-khoz — a hívó `useId`-ja. */
  listId: string;
}

/** A CÉGKERESŐ — beviteli mező és a találatok listája. */
export function NavCompanyPicker({ editor, listId }: NavCompanyPickerProps) {
  const { t } = useTranslation();
  const { matches, query, setQuery, selectedId, pick } = editor;

  return (
    <>
      <label className="credentials__field" htmlFor={`${listId}-query`}>
        {t('navCredentials.companyLabel')}
      </label>
      <input
        id={`${listId}-query`}
        type="text"
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={selectedId === null ? undefined : `${listId}-${selectedId}`}
        placeholder={t('navCredentials.companyPlaceholder')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {matches.length === 0 ? (
        <p className="empty">{t('navCredentials.companyNoMatch')}</p>
      ) : (
        <div
          className="credentials__list"
          role="listbox"
          id={listId}
          // ⚠️ SAJÁT NÉV, NEM A MEZŐÉ. A `companyLabel` a beviteli mező
          // neve; ugyanazt a listára tenni azt jelenti, hogy a
          // képernyőolvasó KÉT elemet mond ugyanazon a néven — és a saját
          // tesztem is ezen bukott el („Found multiple elements").
          aria-label={t('navCredentials.companyResults')}
        >
          {matches.map((company: NavCredentialStatus) => (
            <div
              key={company.companyId}
              id={`${listId}-${company.companyId}`}
              role="option"
              tabIndex={-1}
              aria-selected={company.companyId === selectedId}
              className={
                company.companyId === selectedId
                  ? 'credentials__item credentials__item--on'
                  : 'credentials__item'
              }
              onMouseDown={(event) => {
                event.preventDefault();
                pick(company);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  pick(company);
                }
              }}
            >
              {/* VALÓDI SŰRŰSÉG: a hosszú cégnév ÉS a 11 jegyű adószám
                  egymás mellett (`CLAUDE.md` §4). Egy „Company A" makett
                  hazudna arról, elfér-e a kettő egy sorban. */}
              <span className="credentials__name">{company.companyName}</span>
              <span className="credentials__tax">{company.taxNumber}</span>
              <span
                className={
                  company.configured ? 'credentials__badge--on' : 'credentials__badge--off'
                }
              >
                {company.configured
                  ? t('navCredentials.configured')
                  : t('navCredentials.notConfigured')}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

interface NavCredentialFieldsProps {
  editor: NavCredentialEditor;
  listId: string;
}

/**
 * A NÉGY MEZŐ — csak kiválasztott cégnél, és a mentés UTÁN nem.
 *
 * A `saved` ágon szándékosan eltűnik: egy ott hagyott, üres űrlap azt
 * sugallná, hogy még van mit beírni, és a következő cég kiválasztása előtt a
 * könyvelő újrakezdené.
 */
export function NavCredentialFields({ editor, listId }: NavCredentialFieldsProps) {
  const { t } = useTranslation();
  const { selected, saved, saving, form, setForm, submit } = editor;

  if (selected === null || saved) {
    return null;
  }

  return (
    <form
      className="credentials__form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <p className="credentials__notice">{t('navCredentials.writeOnlyNotice')}</p>
      {selected.configured ? (
        <p className="credentials__notice credentials__notice--warn">
          {t('navCredentials.overwriteNotice')}
        </p>
      ) : null}

      <label htmlFor={`${listId}-login`}>{t('navCredentials.loginLabel')}</label>
      <input
        id={`${listId}-login`}
        type="text"
        // ⚠️ `autoComplete="off"` MIND A NÉGY MEZŐN. Ezek NEM a
        // felhasználó saját jelszavai, hanem egy ÜGYFÉLCÉG NAV-kulcsai:
        // a böngésző jelszókezelőjébe mentve a könyvelő gépén nyolcvan
        // cég titka gyűlne össze, a mi titkosításunkon KÍVÜL.
        autoComplete="off"
        required
        value={form.login}
        onChange={(event) => setForm({ ...form, login: event.target.value })}
      />
      <p className="credentials__hint">{t('navCredentials.loginHint')}</p>

      <label htmlFor={`${listId}-password`}>{t('navCredentials.passwordLabel')}</label>
      <PasswordInput
        id={`${listId}-password`}
        autoComplete="off"
        required
        value={form.password}
        onChange={(event) => setForm({ ...form, password: event.target.value })}
      />

      <label htmlFor={`${listId}-signing`}>{t('navCredentials.signingKeyLabel')}</label>
      <PasswordInput
        id={`${listId}-signing`}
        autoComplete="off"
        required
        value={form.signingKey}
        onChange={(event) => setForm({ ...form, signingKey: event.target.value })}
      />
      <p className="credentials__hint">{t('navCredentials.signingKeyHint')}</p>

      <label htmlFor={`${listId}-exchange`}>{t('navCredentials.exchangeKeyLabel')}</label>
      <PasswordInput
        id={`${listId}-exchange`}
        autoComplete="off"
        value={form.exchangeKey}
        onChange={(event) => setForm({ ...form, exchangeKey: event.target.value })}
      />
      <p className="credentials__hint">{t('navCredentials.exchangeKeyHint')}</p>

      <button type="submit" disabled={saving}>
        {saving ? t('navCredentials.saving') : t('navCredentials.save')}
      </button>
    </form>
  );
}

/**
 * A MENTÉS NYUGTÁZÁSA — és a visszaút a következő céghez.
 *
 * ⚠️ A `savedFor`-t ÍRJA KI, NEM A KIJELÖLÉST. A kereső mentés közben is
 * kattintható; ha a kijelölés elmozdul, mielőtt a válasz visszaér, a
 * kijelölés MÁS cégre mutat, mint amelyiknek írtunk. A levezetés a
 * `useNavCredentialEditor` `savedFor` mezőjénél áll.
 */
export function NavCredentialSaved({ editor }: { editor: NavCredentialEditor }) {
  const { t } = useTranslation();
  const { saved, savedFor, next } = editor;

  if (!saved || savedFor === null) {
    return null;
  }

  return (
    /* ⚠️ `<output>`, NEM `<p role="status">`. Ugyanaz a mondat: a Biome
       `useSemanticElements` szabálya a szerep-attribútumot hibának veszi, a
       `lint-gate` pedig a figyelmeztetést is. A HANG viszont kell: a mentés
       után a fókuszált gomb eltűnik (az űrlap a `saved` ágon kiesik), és
       enélkül a képernyőolvasó SEMMIT nem mondana egy billentyűzet-első
       felületen (D9). */
    <output className="credentials__done">
      <p>{t('navCredentials.saved', { company: savedFor.companyName })}</p>
      {/* ⚠️ „MÁSIK CÉG", NEM „BEZÁRÁS". Ez a gomb SOSEM zárt be semmit — a
          `next()` a kiválasztást ejti és újratölti a listát. A lapon a
          „Bezárás" felirat kifejezetten hazudna: nincs mit bezárni. */}
      <button type="button" onClick={next}>
        {t('navCredentials.another')}
      </button>
    </output>
  );
}
